/**
 * Composant pour protéger l'accès basé sur les permissions.
 */
import React from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { PermissionCode, PlatformRoleName, OperationalRoleName } from '@/types/permissions';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface PermissionGuardProps {
  children: React.ReactNode;
  permission?: PermissionCode;
  permissions?: PermissionCode[];
  role?: PlatformRoleName | OperationalRoleName;
  roles?: (PlatformRoleName | OperationalRoleName)[];
  requireAll?: boolean; // Si true, nécessite toutes les permissions/rôles
  fallback?: React.ReactNode;
  showError?: boolean;
  className?: string;
}

const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permission,
  permissions = [],
  role,
  roles = [],
  requireAll = false,
  fallback = null,
  showError = true,
  className = ''
}) => {
  const { 
    hasPermission, 
    hasAnyPermission, 
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    loading 
  } = usePermissions();

  // Si en cours de chargement, ne rien afficher
  if (loading) {
    return null;
  }

  let hasAccess = false;

  // Vérifier les permissions
  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions.length > 0) {
    hasAccess = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  }

  // Vérifier les rôles
  if (role) {
    hasAccess = hasRole(role);
  } else if (roles.length > 0) {
    hasAccess = requireAll 
      ? hasAllRoles(roles)
      : hasAnyRole(roles);
  }

  // Si pas de permission/rôle spécifié, autoriser l'accès
  if (!permission && permissions.length === 0 && !role && roles.length === 0) {
    hasAccess = true;
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  // Afficher le fallback ou un message d'erreur
  if (fallback) {
    return <>{fallback}</>;
  }

  if (showError) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Accès refusé
          </h3>
          <p className="text-sm text-gray-500">
            Vous n'avez pas les permissions nécessaires pour accéder à cette fonctionnalité.
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default PermissionGuard;
